import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  FileTextIcon,
  PencilLineIcon,
  Trash2Icon,
  ArrowLeftIcon,
  MailIcon,
  ExternalLinkIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'

interface Attendee {
  email: string
  response_status?: string
}

interface ExternalEvent {
  id: string
  title: string
  description: string | null
  location: string | null
  date: string
  time: string
  duration: number
  start_time: string
  end_time: string
  attendees: Attendee[]
  creator: string | null
  organizer: string | null
  html_link: string | null
}

interface Connection {
  id: number
  calendar_type: string
  google_account_email: string
}

interface ExternalEventShowProps {
  event: ExternalEvent
  connection: Connection
}

export default function ExternalEventShow({ event, connection }: ExternalEventShowProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleDelete = () => {
    router.delete(`/google_calendar/external_events/${event.id}?connection_id=${connection.id}`)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getAttendeeStatusColor = (status?: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      case 'declined':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'tentative':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    }
  }

  return (
    <>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between px-4 lg:px-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => router.visit('/google_calendar')}
                  data-testid="back-to-calendar-button"
                >
                  <ArrowLeftIcon className="size-4" />
                </Button>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">Event Details</h1>
                  <p className="text-muted-foreground">From Google Calendar ({connection.calendar_type})</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    router.visit(`/google_calendar/external_events/${event.id}/edit?connection_id=${connection.id}`)
                  }
                  data-testid="edit-event-button"
                >
                  <PencilLineIcon className="mr-2 size-4" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-event-button"
                >
                  <Trash2Icon className="mr-2 size-4" />
                  Delete
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="grid gap-6 px-4 lg:px-6">
              {/* Event Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Event Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Title */}
                    <div className="flex items-start gap-3">
                      <FileTextIcon className="size-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Title</p>
                        <p className="mt-1">{event.title}</p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-start gap-3">
                      <CalendarIcon className="size-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Date</p>
                        <p className="mt-1">{formatDate(event.date)}</p>
                      </div>
                    </div>

                    {/* Time */}
                    <div className="flex items-start gap-3">
                      <ClockIcon className="size-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Time</p>
                        <p className="mt-1">
                          {event.time} ({event.duration} minutes)
                        </p>
                      </div>
                    </div>

                    {/* Location */}
                    {event.location && (
                      <div className="flex items-start gap-3">
                        <MapPinIcon className="size-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Location</p>
                          <p className="mt-1">{event.location}</p>
                        </div>
                      </div>
                    )}

                    {/* Organizer */}
                    {event.organizer && (
                      <div className="flex items-start gap-3">
                        <UserIcon className="size-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Organizer</p>
                          <p className="mt-1">{event.organizer}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {event.description && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Description</p>
                      <p className="whitespace-pre-wrap rounded-md bg-muted p-3">{event.description}</p>
                    </div>
                  )}

                  {/* Google Calendar Link */}
                  {event.html_link && (
                    <div>
                      <a
                        href={event.html_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline dark:text-blue-400"
                      >
                        <ExternalLinkIcon className="size-4" />
                        View in Google Calendar
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Participants */}
              {event.attendees.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Participants ({event.attendees.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {event.attendees.map((attendee, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                          <div className="flex items-center gap-2">
                            <MailIcon className="size-4 text-muted-foreground" />
                            <span>{attendee.email}</span>
                          </div>
                          {attendee.response_status && (
                            <Badge variant="secondary" className={getAttendeeStatusColor(attendee.response_status)}>
                              {attendee.response_status}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Event"
        description="Are you sure you want to delete this event from Google Calendar? This action cannot be undone."
      />
    </>
  )
}
